import { createFileRoute } from "@tanstack/react-router";

import { CategoriaSeoPage, type CategoriaSeoConfig } from "@/components/site/CategoriaSeoPage";
import { productos } from "@/lib/productos";
import { crearHeadCategoriaSeo } from "@/lib/seo-categorias";

const modelos = productos.filter((producto) => producto.category === "llaveros");

const config: CategoriaSeoConfig = {
  path: "/llavero-nfc-qr",
  breadcrumb: "Llavero NFC + QR",
  eyebrow: "NFC + QR para llevar",
  title: "Llaveros NFC + QR para tu negocio",
  intro:
    "Comparte reseñas de Google, Instagram o WhatsApp desde un llavero compacto. Elige tu modelo y recíbelo configurado con el enlace de tu negocio.",
  category: "llaveros",
  featuredId: "llavero-google-negro",
  benefits: [
    "Google, Instagram o WhatsApp",
    "Formato compacto y fácil de llevar",
    "NFC y QR incluidos",
    "Configurado y probado antes de entregar",
  ],
  sectionLabel: "Conexión portátil",
  sectionTitle: "Tu enlace siempre contigo.",
  sectionCopy:
    "Un llavero NFC permite mostrar un acceso directo durante una venta, una visita o una conversación. Es una alternativa práctica para quienes trabajan en terreno o cambian de lugar durante el día.",
  useCases: [
    {
      titulo: "Reseñas de Google",
      texto:
        "Acerca el llavero al teléfono del cliente y facilita el acceso a la ficha del negocio.",
    },
    {
      titulo: "Perfil de Instagram",
      texto: "Comparte tu cuenta sin dictar el usuario ni pedir que te busquen manualmente.",
    },
    {
      titulo: "Contacto por WhatsApp",
      texto: "Abre una conversación con tu número para cotizaciones o consultas posteriores.",
    },
    {
      titulo: "Trabajo en terreno",
      texto: "Úsalo en entregas, servicios a domicilio, ferias o reuniones con clientes.",
    },
  ],
  faqs: [
    {
      pregunta: "¿Qué puede abrir un llavero NFC?",
      respuesta:
        "Puede abrir el enlace configurado para el modelo elegido, como el perfil de Google, Instagram o una conversación de WhatsApp de tu negocio.",
    },
    {
      pregunta: "¿El llavero incluye código QR?",
      respuesta:
        "Sí. Los modelos incluyen NFC y QR para ofrecer dos formas simples de acceder al enlace.",
    },
    {
      pregunta: "¿Se entrega listo para usar?",
      respuesta:
        "Sí. Nos envías el enlace, programamos el chip NFC, generamos el QR y comprobamos ambos antes de coordinar la entrega.",
    },
    {
      pregunta: "¿Qué modelos de llavero NFC tienen?",
      respuesta:
        "Actualmente puedes elegir llaveros para Google en negro o blanco, Instagram y WhatsApp, sujetos a disponibilidad.",
    },
    {
      pregunta: "¿Hay que pagar una mensualidad?",
      respuesta:
        "No. El llavero funciona con el enlace acordado y no requiere una aplicación ni un plan mensual.",
    },
  ],
  finalTitle: "Elige tu llavero NFC",
  finalCopy:
    "Cuéntanos qué enlace quieres compartir y qué color o modelo prefieres. Te confirmamos disponibilidad y entrega por WhatsApp.",
  whatsappText: "Hola, vi los llaveros NFC de Pyme Visible y quiero cotizar uno para mi negocio.",
};

export const Route = createFileRoute("/llavero-nfc-qr")({
  component: LlaveroNfcPage,
  head: () =>
    crearHeadCategoriaSeo({
      path: config.path,
      title: "Llaveros NFC + QR para Google, Instagram y WhatsApp | Pyme Visible",
      description:
        "Compra llaveros NFC con QR para Google, Instagram o WhatsApp. Configurados y listos para usar en Chile desde $10.000, sin mensualidades.",
      ogTitle: "Llaveros NFC + QR para negocios | Pyme Visible",
      ogDescription:
        "Modelos NFC + QR para compartir Google, Instagram o WhatsApp desde un llavero compacto.",
      image:
        modelos.find((producto) => producto.id === config.featuredId)?.image ??
        modelos[0]?.image ??
        "/productos/llavero-whatsapp.webp",
      breadcrumb: config.breadcrumb,
      products: modelos,
      faqs: config.faqs,
    }),
});

function LlaveroNfcPage() {
  return <CategoriaSeoPage config={config} />;
}
